import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { createConnectTransport } from '@connectrpc/connect-web'
import { createClient, ConnectError, Code } from '@connectrpc/connect'
import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import { CortadoService } from '../../gen/cortado/v1/cortado_pb'
import { GetMeRequestSchema, GetMeResponseSchema } from '../../gen/cortado/v1/users_pb'
import { getMe } from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import { queryClient } from '../../integrations/tanstack-query/root-provider'
import { connectTransport, sentryRpcInterceptor } from '../../lib/connect-transport'
import { authStore } from '../../stores/auth.store'
import { setSentryUser, captureError } from '../../lib/sentry'
import { INACTIVE_ACCOUNT_MESSAGE, isInactiveAccountError } from '../../lib/inactive-account'
import { APP_HOME_PATH } from '@/lib/app-home'

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallback,
})

function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState('Completing authentication...')

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get current URL
        const url = new URL(window.location.href)

        // Check for errors in query params
        const errorParam = url.searchParams.get('error')
        const errorDescription = url.searchParams.get('error_description')

        if (errorParam) {
          // Check if this is a "signups not allowed" error from a Supabase invite
          const isSignupDisabled = errorDescription?.toLowerCase().includes('signups not allowed') ||
            errorDescription?.toLowerCase().includes('signup is disabled')

          if (isSignupDisabled) {
            setError('This invite link could not be processed. Please ask your administrator to send you a new invitation from Cortado.')
          } else {
            setError(errorDescription || 'Authentication failed')
          }
          setTimeout(() => navigate({ to: '/login' }), 5000)
          return
        }

        // Supabase client automatically handles session with detectSessionInUrl: true
        // Just verify the session was created
        const { data: { session: initialSession }, error: sessionError } = await supabase.auth.getSession()

        if (sessionError) {
          console.error('Session error:', sessionError)
          setError('Failed to establish session')
          setTimeout(() => navigate({ to: '/login' }), 3000)
          return
        }

        // If no session but hash contains tokens, Supabase may still be processing
        // Wait briefly and retry once
        let session = initialSession
        if (!session && window.location.hash.includes('access_token')) {
          await new Promise(resolve => setTimeout(resolve, 500))
          const retry = await supabase.auth.getSession()
          if (retry.data.session) {
            session = retry.data.session
          }
        }

        if (session) {
          // Update authStore immediately so subsequent pages have the session
          // This prevents race conditions where the next page loads before
          // the global auth state listener has processed the new session
          authStore.setState((state) => ({
            ...state,
            session,
            user: session.user,
            isLoading: false,
          }))

          // Set Sentry user context
          setSentryUser({
            id: session.user.id,
            email: session.user.email,
          })

          // Check if this is an invite flow - user needs to set password
          const hashParams = new URLSearchParams(window.location.hash.substring(1))
          const type = hashParams.get('type')

          if (type === 'invite') {
            // Invite flow - redirect to set password page
            navigate({ to: '/set-password' })
            return
          }

          setStatus('Checking your profile...')

          // Create transport with auth
          const transport = createConnectTransport({
            baseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8080',
            interceptors: [
              (next) => async (req) => {
                req.header.set('Authorization', `Bearer ${session.access_token}`)
                return next(req)
              },
              sentryRpcInterceptor,
            ],
          })

          const client = createClient(CortadoService, transport)

          // Check user profile and onboarding state
          try {
            const userResponse = await client.getMe(create(GetMeRequestSchema, {}))

            // Use the onboarding state from the response for routing decisions
            if (!userResponse.profileComplete) {
              // Magic link invites: user needs to set password first
              if (type === 'magiclink') {
                navigate({ to: '/set-password' })
                return
              }
              navigate({ to: '/onboarding/user' })
              return
            }

            if (!userResponse.hasOrganization) {
              navigate({ to: '/onboarding/organization' })
              return
            }

            // Seed TanStack Query cache with the data we already fetched
            // This prevents duplicate API calls when the dashboard loads
            const getMeQueryKey = createConnectQueryKey({
              schema: getMe,
              transport: connectTransport,
              input: {},
              cardinality: 'finite',
            })
            queryClient.setQueryData(
              getMeQueryKey,
              create(GetMeResponseSchema, {
                user: userResponse.user,
                profileComplete: userResponse.profileComplete,
                hasOrganization: userResponse.hasOrganization,
              })
            )

            // All good, go to dashboard
            navigate({ to: APP_HOME_PATH })
          } catch (apiError) {
            console.error('API error during onboarding check:', apiError)

            if (isInactiveAccountError(apiError)) {
              await supabase.auth.signOut()
              setError(INACTIVE_ACCOUNT_MESSAGE)
              return
            }

            captureError(apiError instanceof Error ? apiError : new Error(String(apiError)), { context: 'auth-callback-onboarding-check' })

            if (apiError instanceof ConnectError && apiError.code === Code.PermissionDenied) {
              // User authenticated with Supabase but signups are disabled - sign them out
              await supabase.auth.signOut()
              setError('Signups are currently disabled. Please contact your administrator.')
              setTimeout(() => navigate({ to: '/login' }), 3000)
              return
            }

            // For any errors, go to dashboard and let the app handle it
            navigate({ to: APP_HOME_PATH })
          }
        } else {
          // No session found
          setError('Authentication failed - no session')
          setTimeout(() => navigate({ to: '/login' }), 3000)
        }
      } catch (err) {
        console.error('Callback error:', err)
        captureError(err instanceof Error ? err : new Error(String(err)), { context: 'auth-callback' })
        setError('An unexpected error occurred')
        setTimeout(() => navigate({ to: '/login' }), 3000)
      }
    }

    handleCallback()
  }, [navigate])

  if (error) {
    const isInactiveError = error === INACTIVE_ACCOUNT_MESSAGE

    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4">
        <h1 className="display-heading text-2xl font-semibold tracking-[-0.03em] text-destructive">Authentication Error</h1>
        <p className="text-muted-foreground">{error}</p>
        {!isInactiveError && (
          <p className="text-sm text-muted-foreground">Redirecting to login...</p>
        )}
      </div>
    )
  }

  return (
    <div className="flex min-h-svh items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  )
}
